import type {
  ApiCliAuthRequest,
  ApiOrgRuntime,
  ApiCliDeviceAuthRequest,
  ApiAdminDnsStatuses,
  ApiAdminLogsResponse,
  ApiDailyUniqueUsersResponse,
  ApiDeploysHistogramResponse,
  ApiLogsHistogramResponse,
  ApiMetricsGroupBy,
  ApiMetricsHistogramResponse,
  ApiMetricsResolution,
  ApiMetricsStatusClass,
  ApiMetricsMethod,
  ApiMetricsTopResponse,
  ApiMetricsTopByAppResponse,
  ApiAppMetricsTopUsersResponse,
  ApiAppMetricsSummary,
  ApiNamespaceMetricsTopUsersResponse,
  ApiUserMetricsTopAppsResponse,
  ApiUserOwnedAppMetricsResponse,
  ApiMyMetricsResponse,
  ApiApp,
  ApiAuditEntry,
  ApiDeploy,
  ApiDeployFile,
  ApiDeployFileContent,
  ApiDnsStatus,
  ApiDnsSyncResult,
  ApiEnvVar,
  ApiExternalShare,
  ApiExternalShareLink,
  ApiExternalShareStats,
  ApiAppExternalShareLogs,
  ApiGrant,
  ApiGroup,
  ApiCohortAppAccess,
  ApiGroupKind,
  ApiGroupMember,
  ApiGroupReferences,
  ApiIdentity,
  ApiMcpClient,
  ApiMcpConsentDetails,
  ApiMcpGrant,
  ApiMember,
  ApiNamespace,
  ApiNamespaceGroupViewer,
  ApiPreviewUser,
  ApiReadinessResponse,
  ApiReconciliationFinding,
  ApiReconciliationIncident,
  ApiReconciliationIncidentSummary,
  ApiReconciliationResolution,
  ApiReconciliationRunSummary,
  ApiRuntimeLog,
  ApiTimelineResponse,
  ApiUserDetail,
  ApiUserDebugResponse,
  ApiUserGroupRef,
  ApiUserMembership,
  ApiPlatformAdmin,
  ApiCapabilityRow,
  ApiAdminOverviewResponse,
  ApiUserSearchResult,
  ApiUserSummary,
  ApiVirtualGroup,
  ApiVirtualRule,
  ApiLlmAlias,
  ApiPersonalAccessToken,
  ApiTokenProvenance,
  ApiWebhookTokenActivity,
  ApiStorageUsage,
  ApiStorageQueryResult,
  ApiStorageTablesResult,
  ApiBlobList,
  ApiKvList,
  ApiKvValue,
  ApiAppLlmUsage,
  ApiAppLlmUsageMe,
  ApiMyLlmUsage,
  ApiAdminLlmUsage,
  ApiAppInventory,
  ApiMigrateSuccess,
  Sharing,
  CliInstallGrantResponse,
  GuestGroupsResponse,
  SetUserClassInput,
  UserClassRepairPreview,
  UserClassRepairApplyInput,
  UserClassRepairApplyResponse,
  UserClassResetInput,
  UserClassResetResponse,
  GuestGroupImpact,
} from './types';
import { ApiError } from './types';
import { resolveFixture } from './fixtures';
import type { McpToolOverride, UserClass } from '@mg/shared/types';
import type {
  OrgCatalog,
  OrgTree,
  OrgUser,
  OrgSearch,
  OrgGroupDefinition,
  OrgGroup,
  OrgGroups,
} from '@mg/shared/org';
import { requestProgress } from '$lib/state/request-progress.svelte';
import { redirectToLogin } from './auth-redirect';

export type CliLoopbackAuthRequest = ApiCliAuthRequest;

export interface CliLoopbackAuthApproval {
  ok: true;
  callback_port: number;
  callback_state: string;
  handoff_code: string;
  approved_at?: number;
}

// When true, the in-flight call must NOT drive the `requestProgress` counter.
// The `apiClientQuiet` proxy below flips this on for the synchronous duration
// of a method call. `requestRaw` snapshots it before its first `await`, so the
// value in effect at call time is the one captured regardless of when the
// request later resolves.
//
// Why this exists: SvelteKit runs route `load()` functions on hover-preload
// (`data-sveltekit-preload-data="hover"` in app.html). Those loaders call the
// API, which would otherwise light the top-bar loading indicator for a
// navigation the user only *might* make. Loaders use `apiClientQuiet` so a
// real navigation still shows the bar (via SvelteKit's `navigating` store)
// while a speculative preload stays silent.
let suppressProgress = false;

// Invoked (fire-and-forget) on every 403. current-identity registers a
// throttled /me re-read so a downgrade the server already enforces also
// retires any cached member UI surface on the next authority read.
let forbiddenHook: (() => void) | null = null;
export function onForbiddenResponse(hook: () => void): void {
  forbiddenHook = hook;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const { body: parsed } = await requestRaw<T>(method, path, body);
  return parsed;
}

/**
 * Same as `request` but also surfaces the HTTP status and a few specific
 * response headers callers may need to branch on (e.g. `x-mg-deploy-status`
 * to distinguish a freshly-created deploy from a dedupe-shortcut response).
 */
async function requestRaw<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<{ body: T; status: number; deployStatus: string | null }> {
  // Preview/fixture mode: the standalone PR-preview deploy has no backend, so
  // serve canned data instead of hitting the network. Behind a static
  // `import.meta.env.VITE_PREVIEW` literal — Rollup drops this branch (and the
  // imported fixtures module) from normal builds.
  //
  // The `=== true` is load-bearing, not redundant: a real Vite build textually
  // substitutes the define (→ boolean `false`/`true`, so the comparison
  // constant-folds and the branch is still tree-shaken), but Vitest exposes the
  // define as a runtime `import.meta.env` *string* (`"false"`, which is truthy).
  // Comparing against `true` makes both worlds agree — without it every
  // apiClient call short-circuits into fixtures under vitest and never reaches
  // the mocked fetch.
  //
  // The macrotask delay matters: resolving synchronously lets the home page's
  // load() revalidate + invalidate() fire inside the same tick as SvelteKit's
  // initial hydration, which wedges the mount (blank page). A tiny setTimeout
  // pushes resolution past first paint, mirroring real network timing.
  if (import.meta.env.VITE_PREVIEW === true) {
    const data = resolveFixture(method, path, body) as T;
    await new Promise((r) => setTimeout(r, 1));
    return { body: data, status: 200, deployStatus: null };
  }
  // Snapshot the suppression flag synchronously, before any await — the proxy
  // that sets it only holds it for the synchronous portion of the call.
  const countProgress = !suppressProgress;
  if (countProgress) requestProgress.start();
  try {
    const init: RequestInit = {
      method,
      credentials: 'include',
      headers: body ? { 'content-type': 'application/json' } : {},
    };
    if (body !== undefined) init.body = JSON.stringify(body);
    const res = await fetch(path, init);
    if (!res.ok) {
      // 401 = the session is gone. Centralized re-auth: bounce the whole
      // window to login (see auth-redirect.ts) regardless of which call or
      // page hit it. 403 (authenticated but forbidden) is left to the caller
      // to surface as an error — it must NOT redirect.
      if (res.status === 401) redirectToLogin();
      if (res.status === 403) forbiddenHook?.();
      const text = await res.text();
      throw new ApiError(res.status, text);
    }
    const deployStatus = res.headers.get('x-mg-deploy-status');
    if (res.status === 204) {
      return { body: undefined as T, status: res.status, deployStatus };
    }
    const ct = res.headers.get('content-type') ?? '';
    if (!ct.includes('application/json')) {
      return { body: undefined as T, status: res.status, deployStatus };
    }
    const parsed = (await res.json()) as T;
    return { body: parsed, status: res.status, deployStatus };
  } finally {
    if (countProgress) requestProgress.end();
  }
}

export const apiClient = {
  // Auth + identity
  async me(): Promise<ApiIdentity> {
    return request<ApiIdentity>('GET', '/api/v1/me');
  },

  async prepareCliInstall(): Promise<CliInstallGrantResponse> {
    return request('POST', '/api/v1/cli-install-grants');
  },

  // Platform limits — server-side caps the Console mirrors into its UI hints
  // and pre-flight checks. Returns whatever the control plane currently
  // enforces; clients can refresh whenever they want to pick up ops bumps.
  async getLimits(): Promise<{
    deploy: {
      max_file_bytes: number;
      max_bundle_bytes: number;
      max_zipped_bundle_bytes: number;
      max_decompressed_bundle_bytes: number;
      max_file_count: number;
    };
    llm: { max_body_bytes: number };
    blob: { max_put_bytes: number };
  }> {
    return request('GET', '/api/v1/limits');
  },

  // Namespaces
  async listNamespaces(): Promise<{ namespaces: ApiNamespace[] }> {
    return request('GET', '/api/v1/namespaces');
  },
  async getNamespace(id: string): Promise<{ namespace: ApiNamespace }> {
    return request('GET', `/api/v1/namespaces/${id}`);
  },
  async createNamespace(body: {
    tier: 'team' | 'brand';
    stub: string;
    display_name?: string;
    description?: string;
  }): Promise<{ namespace: ApiNamespace }> {
    return request('POST', '/api/v1/namespaces', body);
  },
  async patchNamespace(
    id: string,
    body: Partial<{
      display_name: string;
      description: string;
      home_app_id: string | null;
    }>,
  ): Promise<{ namespace: ApiNamespace }> {
    return request('PATCH', `/api/v1/namespaces/${id}`, body);
  },
  async listMembers(
    nsId: string,
  ): Promise<{ members: ApiMember[]; group_viewers: ApiNamespaceGroupViewer[] }> {
    return request('GET', `/api/v1/namespaces/${nsId}/members`);
  },
  async putMember(nsId: string, userId: string, role: string): Promise<void> {
    return request('PUT', `/api/v1/namespaces/${encodeURIComponent(nsId)}/members/${encodeURIComponent(userId)}`, { role });
  },
  async removeMember(nsId: string, userId: string): Promise<void> {
    return request('DELETE', `/api/v1/namespaces/${nsId}/members/${userId}`);
  },
  async addNamespaceGroupViewer(nsId: string, groupId: string): Promise<void> {
    return request(
      'PUT',
      `/api/v1/namespaces/${encodeURIComponent(nsId)}/groups/${encodeURIComponent(groupId)}`,
    );
  },
  async removeNamespaceGroupViewer(nsId: string, groupId: string): Promise<void> {
    return request(
      'DELETE',
      `/api/v1/namespaces/${encodeURIComponent(nsId)}/groups/${encodeURIComponent(groupId)}`,
    );
  },

  // Apps
  async listApps(
    nsId: string,
    opts: { includeArchived?: boolean } = {},
  ): Promise<{ apps: ApiApp[] }> {
    const qs = opts.includeArchived ? '?include_archived=1' : '';
    return request('GET', `/api/v1/namespaces/${nsId}/apps${qs}`);
  },
  // Bulk list across every namespace the caller can see — single round-trip
  // replacing the per-namespace fan-out previously done by the home page.
  async listAllApps(opts: { includeArchived?: boolean } = {}): Promise<{ apps: ApiApp[] }> {
    const qs = opts.includeArchived ? '?include_archived=1' : '';
    return request('GET', `/api/v1/apps${qs}`);
  },
  async getApp(id: string): Promise<{ app: ApiApp }> {
    return request('GET', `/api/v1/apps/${id}`);
  },
  async createApp(
    nsId: string,
    body: {
      name: string;
      kind: 'static' | 'worker' | 'mcp';
      sharing: Sharing;
      description?: string;
      config?: Record<string, unknown>;
      resource_version?: 'v1' | 'v2';
      resource_scope?: 'app' | 'user';
      write_protection?: 'open' | 'owner-only';
      /** Opt the new app into platform.sandbox (worker apps). Default false. */
      sandbox_enabled?: boolean;
      /** Opt the new app into /__mg/platform/container (static + worker). Default false. */
      container_enabled?: boolean;
      /** Worker/mcp only. Omit to take the server default (restricted for new worker/mcp apps). */
      http_data_access?: 'open' | 'restricted';
      /** MCP apps only. Overrides config.mcp.transport when both are present. */
      mcp_transport?: 'http' | 'tunnel';
      /** Worker apps only. */
      url_token_enabled?: boolean;
      confirmation_req_id?: string;
    },
  ): Promise<{ app: ApiApp }> {
    return request('POST', `/api/v1/namespaces/${nsId}/apps`, body);
  },
  async patchApp(
    id: string,
    body: Partial<{
      description: string;
      sharing: Sharing;
      /** Exact app slug entered in the human org-wide publishing dialog. */
      sharing_confirmation_slug: string;
      resource_version: 'v1' | 'v2';
      resource_scope: 'app' | 'user';
      write_protection: 'open' | 'owner-only';
      /** Toggle platform.sandbox / /__mg/platform/sandbox (worker apps). */
      sandbox_enabled: boolean;
      /** Toggle /__mg/platform/container (static + worker apps). */
      container_enabled: boolean;
      /** Platform-admin-only stored directory opt-in. */
      org_directory_enabled: boolean;
      /** Toggle webhook URL-token auth on /__webhook/ (worker apps). */
      url_token_enabled: boolean;
      /** Flip the MCP gateway between public HTTP and tunnel-only (mcp apps). */
      mcp_transport: 'http' | 'tunnel';
      /** Close the public HTTP sql/kv data surface (worker + mcp apps). */
      http_data_access: 'open' | 'restricted';
    }>,
  ): Promise<{ app: ApiApp }> {
    return request('PATCH', `/api/v1/apps/${id}`, body);
  },
  async archiveApp(id: string): Promise<{ app: ApiApp }> {
    return request('POST', `/api/v1/apps/${id}/archive`);
  },
  async reactivateApp(id: string): Promise<{ app: ApiApp }> {
    return request('POST', `/api/v1/apps/${id}/reactivate`);
  },
  async purgeApp(id: string): Promise<{ app: { id: string; state: 'purged' } }> {
    return request('POST', `/api/v1/apps/${id}/purge`);
  },

  // Workspace migration (Plan 3) — pre-flight inventory + the destructive
  // POST. Inventory is a pure read used both for "what will move" UI and for
  // the destination-name-conflict preflight; the optional query param folds
  // both calls into one round-trip when a destination is selected.
  async getAppInventory(
    id: string,
    opts: { destinationNamespaceId?: string; userId?: string } = {},
  ): Promise<ApiAppInventory> {
    const q = new URLSearchParams();
    if (opts.destinationNamespaceId) q.set('destination_namespace_id', opts.destinationNamespaceId);
    if (opts.userId) q.set('user_id', opts.userId);
    const qs = q.toString();
    return request('GET', `/api/v1/apps/${id}/inventory${qs ? `?${qs}` : ''}`);
  },
  async migrateApp(
    id: string,
    body: {
      destination_namespace_id: string;
      new_name?: string;
      confirm_sharing_downgrade?: boolean;
    },
  ): Promise<ApiMigrateSuccess> {
    return request('POST', `/api/v1/apps/${id}/migrate`, body);
  },

  // MCP per-tool overrides (Plan H Phase 2) — persists across deploys; applied
  // by the MCP gateway at tools/list (filter + description override) and
  // tools/call (enforce enabled + privileged).
  async listToolOverrides(appId: string): Promise<{ overrides: McpToolOverride[] }> {
    return request('GET', `/api/v1/apps/${appId}/tool-overrides`);
  },
  async putToolOverride(
    appId: string,
    toolName: string,
    body: {
      enabled?: boolean;
      privileged?: boolean;
      description_override?: string | null;
    },
  ): Promise<{ override: McpToolOverride }> {
    return request(
      'PUT',
      `/api/v1/apps/${appId}/tool-overrides/${encodeURIComponent(toolName)}`,
      body,
    );
  },

  // Env vars
  async listEnv(appId: string): Promise<{ env: ApiEnvVar[] }> {
    return request('GET', `/api/v1/apps/${appId}/env`);
  },
  async putEnv(
    appId: string,
    key: string,
    body: { value: string; visibility: 'public' | 'private' },
  ): Promise<{ env: ApiEnvVar }> {
    return request('PUT', `/api/v1/apps/${appId}/env/${encodeURIComponent(key)}`, body);
  },
  async deleteEnv(appId: string, key: string): Promise<void> {
    return request('DELETE', `/api/v1/apps/${appId}/env/${encodeURIComponent(key)}`);
  },

  // Storage observability — usage (manager) and live query (admin-only).
  async getStorageUsage(appId: string, opts?: { userId?: string }): Promise<ApiStorageUsage> {
    const q = opts?.userId ? `?user_id=${encodeURIComponent(opts.userId)}` : '';
    return request('GET', `/api/v1/apps/${appId}/storage/usage${q}`);
  },
  async runStorageQuery(
    appId: string,
    body: { query: string; bindings?: unknown[]; userId?: string },
  ): Promise<ApiStorageQueryResult> {
    const q = body.userId ? `?user_id=${encodeURIComponent(body.userId)}` : '';
    return request('POST', `/api/v1/apps/${appId}/storage/query${q}`, {
      query: body.query,
      ...(body.bindings ? { bindings: body.bindings } : {}),
    });
  },
  async listStorageTables(appId: string): Promise<ApiStorageTablesResult> {
    return request('GET', `/api/v1/apps/${appId}/storage/tables`);
  },
  async runStorageSql(
    appId: string,
    body: { query: string; bindings?: unknown[] },
  ): Promise<ApiStorageQueryResult> {
    return request('POST', `/api/v1/apps/${appId}/storage/sql`, {
      query: body.query,
      ...(body.bindings ? { bindings: body.bindings } : {}),
    });
  },
  async listBlobs(
    appId: string,
    opts?: { prefix?: string; cursor?: string },
  ): Promise<ApiBlobList> {
    const q = new URLSearchParams();
    if (opts?.prefix) q.set('prefix', opts.prefix);
    if (opts?.cursor) q.set('cursor', opts.cursor);
    const qs = q.toString();
    return request('GET', `/api/v1/apps/${appId}/storage/blobs${qs ? `?${qs}` : ''}`);
  },
  blobUrl(appId: string, key: string): string {
    return `/api/v1/apps/${appId}/storage/blob/${encodeURIComponent(key)}`;
  },
  async listKv(appId: string, opts?: { prefix?: string }): Promise<ApiKvList> {
    const q = opts?.prefix ? `?prefix=${encodeURIComponent(opts.prefix)}` : '';
    return request('GET', `/api/v1/apps/${appId}/storage/kv${q}`);
  },
  async getKvValue(appId: string, key: string): Promise<ApiKvValue> {
    return request('GET', `/api/v1/apps/${appId}/storage/kv-value/${encodeURIComponent(key)}`);
  },

  // LLM usage dashboards — read-only aggregates from llm-usage's D1.
  async getAppLlmUsage(appId: string, opts?: { rangeDays?: number }): Promise<ApiAppLlmUsage> {
    const q = opts?.rangeDays ? `?range=${opts.rangeDays}d` : '';
    return request('GET', `/api/v1/apps/${appId}/llm-usage${q}`);
  },
  async getAppLlmUsageMe(appId: string, opts?: { rangeDays?: number }): Promise<ApiAppLlmUsageMe> {
    const q = opts?.rangeDays ? `?range=${opts.rangeDays}d` : '';
    return request('GET', `/api/v1/apps/${appId}/llm-usage/me${q}`);
  },
  async getMyLlmUsage(opts?: { rangeDays?: number }): Promise<ApiMyLlmUsage> {
    const q = opts?.rangeDays ? `?range=${opts.rangeDays}d` : '';
    return request('GET', `/api/v1/me/llm-usage${q}`);
  },
  async getAdminLlmUsage(opts?: { rangeDays?: number }): Promise<ApiAdminLlmUsage> {
    const q = opts?.rangeDays ? `?range=${opts.rangeDays}d` : '';
    return request('GET', `/api/v1/admin/llm-usage${q}`);
  },

  // Deploys
  async listDeploys(appId: string): Promise<{ deploys: ApiDeploy[] }> {
    return request('GET', `/api/v1/apps/${appId}/deploys`);
  },
  async createDeploy(
    appId: string,
    body: {
      kind: 'static' | 'worker' | 'mcp';
      manifest: Record<string, unknown>;
      bundle_size_bytes: number;
      bundle_sha256: string;
      notes?: string;
      /** Set for static deploys whose files were pre-streamed via
       *  beginDeployUpload + putDeployUploadFile. `files[]` then carry metadata
       *  only (no `content`). */
      upload_id?: string;
      files?: Array<{
        path: string;
        size: number;
        sha256: string;
        content?: string;
        content_type?: string;
      }>;
      confirmation_req_id?: string;
    },
  ): Promise<{ deploy: ApiDeploy; unchanged: boolean }> {
    // The server may dedupe a re-upload of an identical bundle and return
    // 200 with the existing deploy + `x-mg-deploy-status: unchanged` instead
    // of 201 + a new deploy id. Surface that so the UI can skip the
    // propagation poll and tell the user "no changes uploaded" rather than
    // pretending to deploy.
    const res = await requestRaw<{ deploy: ApiDeploy }>(
      'POST',
      `/api/v1/apps/${appId}/deploys`,
      body,
    );
    return {
      deploy: res.body.deploy,
      unchanged: res.deployStatus === 'unchanged',
    };
  },
  /**
   * Begin a streaming static deploy. Returns an `upload_id` the caller then
   * PUTs each file under via `putDeployUploadFile`, before committing with
   * `createDeploy({ ..., upload_id })`. Static-only — worker/mcp keep the
   * inline base64 path.
   */
  async beginDeployUpload(appId: string): Promise<{ upload_id: string }> {
    return request('POST', `/api/v1/apps/${appId}/deploys/uploads`, {});
  },
  /**
   * Stream one file's bytes to STAGING for a pending upload. Bypasses the
   * JSON-only `request`/`requestRaw` helpers: the body is the raw Blob, so a
   * large file never gets base64-encoded or buffered as a string. The session
   * cookie authorizes via `credentials: 'include'`, matching every other call.
   */
  async putDeployUploadFile(
    appId: string,
    uploadId: string,
    file: { path: string; sha: string; contentType: string; blob: Blob },
    signal?: AbortSignal,
  ): Promise<{ path: string; size: number }> {
    const url = `/api/v1/apps/${appId}/deploys/uploads/${uploadId}?path=${encodeURIComponent(
      file.path,
    )}`;
    const res = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'content-type': file.contentType || 'application/octet-stream',
        'x-mg-sha256': file.sha,
      },
      body: file.blob,
      ...(signal && { signal }),
    });
    if (!res.ok) {
      if (res.status === 401) redirectToLogin();
      throw new ApiError(res.status, await res.text());
    }
    return (await res.json()) as { path: string; size: number };
  },
  async rollbackDeploy(
    appId: string,
    deployId: string,
    opts?: { restore_data?: boolean },
  ): Promise<{ deploy: ApiDeploy; restored_data?: boolean; snapshot_set_id?: string | null }> {
    return request(
      'POST',
      `/api/v1/apps/${appId}/deploys/${deployId}/rollback`,
      opts?.restore_data ? { restore_data: true } : undefined,
    );
  },
  async getDeploy(appId: string, deployId: string): Promise<{ deploy: ApiDeploy }> {
    return request('GET', `/api/v1/apps/${appId}/deploys/${deployId}`);
  },
  async getDeployReadiness(appId: string, deployId: string): Promise<ApiReadinessResponse> {
    return request('GET', `/api/v1/apps/${appId}/deploys/${deployId}/readiness`);
  },
  async listDeployFiles(appId: string, deployId: string): Promise<{ files: ApiDeployFile[] }> {
    return request('GET', `/api/v1/apps/${appId}/deploys/${deployId}/files`);
  },
  async getDeployFile(
    appId: string,
    deployId: string,
    path: string,
  ): Promise<ApiDeployFileContent> {
    return request(
      'GET',
      `/api/v1/apps/${appId}/deploys/${deployId}/files/content?path=${encodeURIComponent(path)}`,
    );
  },
  // Same-origin URL for the whole-deploy zip download. Used as a plain <a href
  // download> so the session cookie rides along and the browser names the file
  // from the response's Content-Disposition.
  downloadDeployUrl(appId: string, deployId: string): string {
    return `/api/v1/apps/${appId}/deploys/${deployId}/download`;
  },

  // Grants
  async listGrants(appId: string): Promise<{ grants: ApiGrant[] }> {
    return request('GET', `/api/v1/apps/${appId}/grants`);
  },
  async addGrant(appId: string, email: string, role?: 'reader' | 'writer'): Promise<void> {
    return request(
      'PUT',
      `/api/v1/apps/${appId}/share/${encodeURIComponent(email)}`,
      role ? { role } : undefined,
    );
  },
  async addGroupGrant(appId: string, groupId: string, role?: 'reader' | 'writer'): Promise<void> {
    // The mock route is PUT /api/v1/apps/<id>/share/<email>?kind=group&id=<groupId>.
    // <email> path segment is required by the route template but ignored by the handler when kind=group.
    // Use a sentinel '_group' for the path segment.
    const url = `/api/v1/apps/${appId}/share/_group?kind=group&id=${encodeURIComponent(groupId)}`;
    return request('PUT', url, role ? { role } : undefined);
  },
  async removeGrant(appId: string, principalId: string): Promise<void> {
    return request('DELETE', `/api/v1/apps/${appId}/share/${principalId}`);
  },
  async patchGrantRole(
    appId: string,
    principalId: string,
    role: 'reader' | 'writer',
  ): Promise<void> {
    return request('PATCH', `/api/v1/apps/${appId}/share/${principalId}`, { role });
  },
  async addAppGrantByEmail(
    appId: string,
    email: string,
    role?: 'reader' | 'writer',
  ): Promise<void> {
    await request(
      'PUT',
      `/api/v1/apps/${appId}/share/${encodeURIComponent(email)}`,
      role ? { role } : undefined,
    );
  },
  async removeAppPendingGrant(appId: string, email: string): Promise<void> {
    await request('DELETE', `/api/v1/apps/${appId}/share/pending/${encodeURIComponent(email)}`);
  },

  // External shares (Share Door). `sid` is the external_shares row id (es_…),
  // NOT the public share_id — the CP routes resolve it via getExternalShareById.
  // The create/link responses carry the one-time signed link; it is never
  // refetchable (list/get never return it).
  async createExternalShare(
    appId: string,
    body: {
      display_name?: string;
      note?: string;
      not_after_ms?: number;
      link_ttl_sec?: number;
    } = {},
  ): Promise<{ share: ApiExternalShare; link: string }> {
    return request('POST', `/api/v1/apps/${appId}/external-share`, body);
  },
  async listExternalShares(appId: string): Promise<{ shares: ApiExternalShare[] }> {
    return request('GET', `/api/v1/apps/${appId}/external-share`);
  },
  /** App-level PAUSE: soft-disable every active share (reversible). */
  async pauseExternalSharing(appId: string): Promise<{ paused: number }> {
    return request('POST', `/api/v1/apps/${appId}/external-share/pause`);
  },
  /** App-level STOP: archive every share (drops bytes + links, keeps logs). Terminal. */
  async stopExternalSharing(appId: string): Promise<{ stopped: number }> {
    return request('POST', `/api/v1/apps/${appId}/external-share/stop`);
  },
  async republishExternalShare(appId: string, sid: string): Promise<{ share: ApiExternalShare }> {
    return request('POST', `/api/v1/apps/${appId}/external-share/${sid}/republish`);
  },
  /** Extend a share's hard expiry to `not_after_ms` (server clamps to [now, now+30d]). */
  async extendExternalShare(
    appId: string,
    sid: string,
    notAfterMs: number,
  ): Promise<{ share: ApiExternalShare }> {
    return request('POST', `/api/v1/apps/${appId}/external-share/${sid}/extend`, {
      not_after_ms: notAfterMs,
    });
  },
  async mintExternalShareLink(
    appId: string,
    sid: string,
    body: { alias?: string; note?: string; ttl_sec?: number } = {},
  ): Promise<{ link: string; prefix: string; link_row: ApiExternalShareLink }> {
    return request('POST', `/api/v1/apps/${appId}/external-share/${sid}/link`, body);
  },
  async listExternalShareLinks(
    appId: string,
    sid: string,
  ): Promise<{ links: ApiExternalShareLink[] }> {
    return request('GET', `/api/v1/apps/${appId}/external-share/${sid}/links`);
  },
  async setExternalShareNote(
    appId: string,
    sid: string,
    note: string,
  ): Promise<{ share: ApiExternalShare }> {
    return request('PATCH', `/api/v1/apps/${appId}/external-share/${sid}/note`, { note });
  },
  async setExternalShareLinkNote(
    appId: string,
    sid: string,
    lid: string,
    note: string,
  ): Promise<{ link: ApiExternalShareLink }> {
    return request('PATCH', `/api/v1/apps/${appId}/external-share/${sid}/links/${lid}/note`, {
      note,
    });
  },
  async listAppExternalShareLogs(
    appId: string,
    opts: {
      share_id?: string;
      link_id?: string;
      session_id?: string;
      from?: number;
      to?: number;
    } = {},
  ): Promise<ApiAppExternalShareLogs> {
    const q = new URLSearchParams();
    if (opts.share_id) q.set('share_id', opts.share_id);
    if (opts.link_id) q.set('link_id', opts.link_id);
    if (opts.session_id) q.set('session_id', opts.session_id);
    if (opts.from !== undefined) q.set('from', String(opts.from));
    if (opts.to !== undefined) q.set('to', String(opts.to));
    const qs = q.toString();
    return request('GET', `/api/v1/apps/${appId}/external-share-logs${qs ? `?${qs}` : ''}`);
  },
  async disableExternalShare(appId: string, sid: string): Promise<{ share: ApiExternalShare }> {
    return request('POST', `/api/v1/apps/${appId}/external-share/${sid}/disable`);
  },
  async enableExternalShare(appId: string, sid: string): Promise<{ share: ApiExternalShare }> {
    return request('POST', `/api/v1/apps/${appId}/external-share/${sid}/enable`);
  },
  async revokeExternalShare(appId: string, sid: string): Promise<{ share: ApiExternalShare }> {
    return request('POST', `/api/v1/apps/${appId}/external-share/${sid}/revoke`);
  },
  /** Archive a share: unpublish (stop serving) but keep the row + access logs. */
  async archiveExternalShare(appId: string, sid: string): Promise<{ share: ApiExternalShare }> {
    return request('POST', `/api/v1/apps/${appId}/external-share/${sid}/archive`);
  },
  /** Archive one minted link (hide from the active list; does not invalidate it — revoke does). */
  async archiveExternalShareLink(
    appId: string,
    sid: string,
    lid: string,
  ): Promise<{ link: ApiExternalShareLink }> {
    return request('POST', `/api/v1/apps/${appId}/external-share/${sid}/links/${lid}/archive`);
  },
  /** Hard-delete a share (rare). Prefer archiveExternalShare, which keeps history. */
  async purgeExternalShare(appId: string, sid: string): Promise<void> {
    return request('DELETE', `/api/v1/apps/${appId}/external-share/${sid}`);
  },
  async externalShareStats(
    appId: string,
    sid: string,
    range?: { from: number; to: number },
  ): Promise<ApiExternalShareStats> {
    const q = range ? `?from=${range.from}&to=${range.to}` : '';
    return request('GET', `/api/v1/apps/${appId}/external-share/${sid}/stats${q}`);
  },

  // Audit
  async listAudit(appId: string): Promise<{ audit: ApiAuditEntry[] }> {
    return request('GET', `/api/v1/apps/${appId}/audit`);
  },
  async listNamespaceAudit(
    nsId: string,
    opts: { limit?: number } = {},
  ): Promise<{ audit: ApiAuditEntry[] }> {
    const q = opts.limit ? `?limit=${opts.limit}` : '';
    return request('GET', `/api/v1/namespaces/${nsId}/audit${q}`);
  },
  async adminListAudit(
    opts: {
      from?: number;
      to?: number;
      limit?: number;
      actor?: string;
      action?: string;
      target_kind?: string;
      target_id?: string;
    } = {},
  ): Promise<{ audit: ApiAuditEntry[] }> {
    const params = new URLSearchParams();
    if (opts.from !== undefined) params.set('from', String(opts.from));
    if (opts.to !== undefined) params.set('to', String(opts.to));
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.actor) params.set('actor', opts.actor);
    if (opts.action) params.set('action', opts.action);
    if (opts.target_kind) params.set('target_kind', opts.target_kind);
    if (opts.target_id) params.set('target_id', opts.target_id);
    const q = params.toString();
    return request('GET', `/api/v1/admin/audit${q ? `?${q}` : ''}`);
  },

  // Users + Groups
  async listGroups(): Promise<{ groups: ApiGroup[] }> {
    return request('GET', '/api/v1/groups');
  },
  async listAdminGroups(): Promise<{ groups: ApiGroup[] }> {
    return request('GET', '/api/v1/admin/groups');
  },
  async getCohortAppAccess(
    selector: { group_id: string } | { audience: 'organization' },
  ): Promise<ApiCohortAppAccess> {
    const query = new URLSearchParams(selector);
    return request('GET', `/api/v1/admin/cohort-app-access?${query}`);
  },
  async getGroup(id: string): Promise<{ group: ApiGroup; members: ApiGroupMember[] }> {
    return request('GET', `/api/v1/groups/${encodeURIComponent(id)}`);
  },
  async createGroup(input: {
    external_id: string;
    display_name: string;
    group_kind: ApiGroupKind;
  }): Promise<{ group: ApiGroup }> {
    return request('POST', '/api/v1/admin/groups', input);
  },
  async addGroupMember(groupId: string, userId: string, role: 'member' | 'admin' = 'member'): Promise<void> {
    return request(
      'PUT',
      `/api/v1/admin/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
      { role },
    );
  },
  async removeGroupMember(groupId: string, userId: string): Promise<void> {
    return request(
      'DELETE',
      `/api/v1/admin/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
    );
  },

  // Self-service group management (Plan: self-service-groups)
  async createGroupSelf(displayName: string): Promise<{ group: ApiGroup }> {
    return request('POST', '/api/v1/groups', { display_name: displayName });
  },
  async renameGroup(groupId: string, displayName: string): Promise<void> {
    return request('PATCH', `/api/v1/groups/${encodeURIComponent(groupId)}`, {
      display_name: displayName,
    });
  },
  async addGroupMemberSelf(groupId: string, userId: string, role: 'member' | 'admin' = 'member'): Promise<void> {
    return request(
      'PUT',
      `/api/v1/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
      { role },
    );
  },
  async removeGroupMemberSelf(groupId: string, userId: string): Promise<void> {
    return request(
      'DELETE',
      `/api/v1/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
    );
  },
  async setGroupMemberRole(
    groupId: string,
    userId: string,
    role: 'member' | 'admin',
  ): Promise<void> {
    return request(
      'PUT',
      `/api/v1/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}/role`,
      { role },
    );
  },
  async getGroupReferences(groupId: string): Promise<ApiGroupReferences> {
    return request('GET', `/api/v1/groups/${encodeURIComponent(groupId)}/references`);
  },
  async deleteGroup(groupId: string): Promise<void> {
    return request('DELETE', `/api/v1/groups/${encodeURIComponent(groupId)}`);
  },
  // Authorized directory responses are not placed in app or namespace caches.
  async getOrgCatalog(
    options: { app_id?: string; namespace_id?: string } = {},
  ): Promise<OrgCatalog> {
    const q = new URLSearchParams();
    if (options.app_id) q.set('app_id', options.app_id);
    if (options.namespace_id) q.set('namespace_id', options.namespace_id);
    return request('GET', `/api/v1/org/catalog${q.size ? `?${q}` : ''}`);
  },
  async getOrgTree(
    query: string,
    options: { depth?: number; brand?: string; department?: string } = {},
  ): Promise<OrgTree> {
    const q = new URLSearchParams({ query });
    if (options.depth !== undefined) q.set('depth', String(options.depth));
    if (options.brand) q.set('brand', options.brand);
    if (options.department) q.set('department', options.department);
    return request('GET', `/api/v1/org/tree?${q}`);
  },
  async getOrgUser(query: string): Promise<OrgUser> {
    return request('GET', `/api/v1/org/user?${new URLSearchParams({ query })}`);
  },
  async searchOrg(
    query: string,
    options: {
      limit?: number;
      brand?: string;
      department?: string;
      app_id?: string;
      namespace_id?: string;
    } = {},
  ): Promise<OrgSearch> {
    const q = new URLSearchParams({ q: query });
    if (options.limit !== undefined) q.set('limit', String(options.limit));
    if (options.brand) q.set('brand', options.brand);
    if (options.department) q.set('department', options.department);
    if (options.app_id) q.set('app_id', options.app_id);
    if (options.namespace_id) q.set('namespace_id', options.namespace_id);
    return request('GET', `/api/v1/org/search?${q}`);
  },
  async listOrgGroups(): Promise<OrgGroups> {
    return request('GET', '/api/v1/org/groups');
  },
  async createOrgGroup(definition: OrgGroupDefinition, appId?: string): Promise<OrgGroup> {
    return request('POST', '/api/v1/org/groups', {
      definition,
      ...(appId ? { app_id: appId } : {}),
    });
  },
  async syncOrg(): Promise<{ groups: number; memberships: number; last_synced_at: number }> {
    return request('POST', '/api/v1/admin/org/sync');
  },
  async orgRuntime(): Promise<ApiOrgRuntime> {
    return request('GET', '/api/v1/admin/org/runtime');
  },
  async retireOrgGroup(id: string): Promise<void> {
    await request('POST', `/api/v1/admin/org/groups/${encodeURIComponent(id)}/retire`);
  },

  async listVirtualGroups(): Promise<{ virtual_groups: ApiVirtualGroup[] }> {
    return request('GET', '/api/v1/admin/virtual-groups');
  },
  async createVirtualGroup(input: {
    external_id: string;
    display_name: string;
    rule: ApiVirtualRule;
  }): Promise<{ virtual_group: ApiVirtualGroup }> {
    return request('POST', '/api/v1/admin/virtual-groups', input);
  },
  async updateVirtualGroup(
    id: string,
    input: { display_name?: string; rule?: ApiVirtualRule },
  ): Promise<{ virtual_group: ApiVirtualGroup }> {
    return request('PATCH', `/api/v1/admin/virtual-groups/${encodeURIComponent(id)}`, input);
  },
  async recomputeVirtualGroup(
    id: string,
  ): Promise<{ virtual_group: ApiVirtualGroup; added: number; removed: number; total: number }> {
    return request('POST', `/api/v1/admin/virtual-groups/${encodeURIComponent(id)}/recompute`);
  },
  async deleteVirtualGroup(id: string): Promise<void> {
    return request('DELETE', `/api/v1/admin/virtual-groups/${encodeURIComponent(id)}`);
  },

  // LLM aliases (admin-only)
  async listLlmAliases(): Promise<{ aliases: ApiLlmAlias[]; default_alias: string | null }> {
    return request('GET', '/api/v1/admin/llm-aliases');
  },
  async createLlmAlias(input: {
    alias_id: string;
    model: string;
    label: string;
    sort_order?: number;
  }): Promise<{ alias: ApiLlmAlias }> {
    return request('POST', '/api/v1/admin/llm-aliases', input);
  },
  async updateLlmAlias(
    aliasId: string,
    input: { model?: string; label?: string; sort_order?: number },
  ): Promise<{ alias: ApiLlmAlias }> {
    return request('PATCH', `/api/v1/admin/llm-aliases/${encodeURIComponent(aliasId)}`, input);
  },
  async deleteLlmAlias(aliasId: string): Promise<void> {
    return request('DELETE', `/api/v1/admin/llm-aliases/${encodeURIComponent(aliasId)}`);
  },
  async setDefaultLlmAlias(aliasId: string): Promise<{ default_alias: string }> {
    return request('PATCH', '/api/v1/admin/llm-aliases/default', { alias_id: aliasId });
  },
  async listUsers(): Promise<{ users: ApiUserSummary[] }> {
    return request('GET', '/api/v1/users');
  },
  async getUser(
    id: string,
  ): Promise<{ user: ApiUserDetail; groups: ApiUserGroupRef[]; memberships: ApiUserMembership[] }> {
    return request('GET', `/api/v1/users/${encodeURIComponent(id)}`);
  },
  async adminGetUserDebug(id: string, options: { cursor?: string; login_history_until?: number } = {}): Promise<ApiUserDebugResponse> {
    const query = new URLSearchParams();
    if (options.cursor !== undefined) query.set('cursor', options.cursor);
    if (options.login_history_until !== undefined) query.set('login_history_until', String(options.login_history_until));
    return request('GET', `/api/v1/admin/users/${encodeURIComponent(id)}/debug${query.size ? `?${query}` : ''}`);
  },
  async searchUsers(q: string): Promise<{ users: ApiUserSearchResult[] }> {
    return request('GET', `/api/v1/users/search?q=${encodeURIComponent(q)}`);
  },
  async listPlatformAdmins(): Promise<{ platform_admins: ApiPlatformAdmin[] }> {
    return request('GET', '/api/v1/admin/platform_admins');
  },
  async addPlatformAdmin(userId: string): Promise<void> {
    return request('PUT', `/api/v1/admin/platform_admins/${encodeURIComponent(userId)}`);
  },
  async removePlatformAdmin(userId: string): Promise<void> {
    return request('DELETE', `/api/v1/admin/platform_admins/${encodeURIComponent(userId)}`);
  },

  // Grantable platform capabilities (currently only 'external-share').
  async listCapabilities(): Promise<{ capabilities: ApiCapabilityRow[] }> {
    return request('GET', '/api/v1/admin/capabilities');
  },
  async grantCapability(userId: string, capability: string): Promise<void> {
    return request(
      'PUT',
      `/api/v1/admin/capabilities/${encodeURIComponent(userId)}/${encodeURIComponent(capability)}`,
    );
  },
  async revokeCapability(userId: string, capability: string): Promise<void> {
    return request(
      'DELETE',
      `/api/v1/admin/capabilities/${encodeURIComponent(userId)}/${encodeURIComponent(capability)}`,
    );
  },
  async adminOverview(): Promise<ApiAdminOverviewResponse> {
    return request('GET', '/api/v1/admin/overview');
  },

  // Logs
  async listLogs(
    appId: string,
    filters: { from?: number; to?: number; request_id?: string; level?: string; path?: string },
  ): Promise<{ logs: ApiRuntimeLog[] }> {
    const q = new URLSearchParams();
    if (filters.from !== undefined) q.set('from', String(filters.from));
    if (filters.to !== undefined) q.set('to', String(filters.to));
    if (filters.request_id) q.set('request_id', filters.request_id);
    if (filters.level) q.set('level', filters.level);
    if (filters.path) q.set('path', filters.path);
    const qs = q.toString();
    return request('GET', `/api/v1/apps/${appId}/logs${qs ? '?' + qs : ''}`);
  },
  async listErrors(
    appId: string,
    filters: { from?: number; to?: number },
  ): Promise<{ errors: ApiRuntimeLog[] }> {
    const q = new URLSearchParams();
    if (filters.from !== undefined) q.set('from', String(filters.from));
    if (filters.to !== undefined) q.set('to', String(filters.to));
    const qs = q.toString();
    return request('GET', `/api/v1/apps/${appId}/errors${qs ? '?' + qs : ''}`);
  },
  async getRequestTimeline(requestId: string, appId?: string): Promise<ApiTimelineResponse> {
    const q = new URLSearchParams();
    if (appId) q.set('app_id', appId);
    const qs = q.toString();
    return request('GET', `/api/v1/requests/${encodeURIComponent(requestId)}${qs ? '?' + qs : ''}`);
  },
  async adminListLogs(filters: {
    from?: number;
    to?: number;
    app_ids?: string[];
    owner_user_id?: string;
    owner_email?: string;
    /** Namespace stub — scopes logs to that namespace's apps. The right filter
     * for namespace-owned (team/brand) apps, which have no individual owner. */
    namespace?: string;
    user_id?: string;
    user_email?: string;
    path?: string;
    method?: string;
    status_min?: number;
    status_max?: number;
    cursor?: string;
    limit?: number;
  }): Promise<ApiAdminLogsResponse> {
    const q = new URLSearchParams();
    if (filters.from !== undefined) q.set('from', String(filters.from));
    if (filters.to !== undefined) q.set('to', String(filters.to));
    if (filters.app_ids) for (const id of filters.app_ids) q.append('app_id', id);
    if (filters.owner_user_id) q.set('owner_user_id', filters.owner_user_id);
    if (filters.owner_email) q.set('owner_email', filters.owner_email);
    if (filters.namespace) q.set('namespace', filters.namespace);
    if (filters.user_id) q.set('user_id', filters.user_id);
    if (filters.user_email) q.set('user_email', filters.user_email);
    if (filters.path) q.set('path', filters.path);
    if (filters.method) q.set('method', filters.method);
    if (filters.status_min !== undefined) q.set('status_min', String(filters.status_min));
    if (filters.status_max !== undefined) q.set('status_max', String(filters.status_max));
    if (filters.cursor) q.set('cursor', filters.cursor);
    if (filters.limit !== undefined) q.set('limit', String(filters.limit));
    const qs = q.toString();
    return request('GET', `/api/v1/admin/logs${qs ? '?' + qs : ''}`);
  },
  async adminLogsHistogram(filters: {
    from?: number;
    to?: number;
    app_ids?: string[];
    owner_user_id?: string;
    owner_email?: string;
  }): Promise<ApiLogsHistogramResponse> {
    const q = new URLSearchParams();
    if (filters.from !== undefined) q.set('from', String(filters.from));
    if (filters.to !== undefined) q.set('to', String(filters.to));
    if (filters.app_ids) for (const id of filters.app_ids) q.append('app_id', id);
    if (filters.owner_user_id) q.set('owner_user_id', filters.owner_user_id);
    if (filters.owner_email) q.set('owner_email', filters.owner_email);
    const qs = q.toString();
    return request('GET', `/api/v1/admin/logs/histogram${qs ? '?' + qs : ''}`);
  },
  async adminDeploysHistogram(filters: {
    from?: number;
    to?: number;
    bucket_ms?: number;
  }): Promise<ApiDeploysHistogramResponse> {
    const q = new URLSearchParams();
    if (filters.from !== undefined) q.set('from', String(filters.from));
    if (filters.to !== undefined) q.set('to', String(filters.to));
    if (filters.bucket_ms !== undefined) q.set('bucket_ms', String(filters.bucket_ms));
    const qs = q.toString();
    return request('GET', `/api/v1/admin/deploys/histogram${qs ? '?' + qs : ''}`);
  },

  async adminDailyUniqueUsers(filters: {
    from: number;
    to: number;
  }): Promise<ApiDailyUniqueUsersResponse> {
    const q = new URLSearchParams();
    q.set('from', String(filters.from));
    q.set('to', String(filters.to));
    return request('GET', `/api/v1/admin/metrics/daily-unique-users?${q.toString()}`);
  },

  // ─── MetricsDO query API ─────────────────────────────────────────────
  async adminMetricsHistogram(filters: {
    from: number;
    to: number;
    resolution?: ApiMetricsResolution | 'auto';
    group_by?: ApiMetricsGroupBy[];
    status_class?: ApiMetricsStatusClass;
    method?: ApiMetricsMethod;
    path_prefix?: string;
    kind?: 'static' | 'worker' | 'mcp' | 'llm';
    app_ids?: string[];
  }): Promise<ApiMetricsHistogramResponse> {
    const q = new URLSearchParams();
    q.set('from', String(filters.from));
    q.set('to', String(filters.to));
    if (filters.resolution) q.set('resolution', filters.resolution);
    if (filters.group_by) for (const g of filters.group_by) q.append('group_by', g);
    if (filters.status_class) q.set('status_class', filters.status_class);
    if (filters.method) q.set('method', filters.method);
    if (filters.path_prefix) q.set('path_prefix', filters.path_prefix);
    if (filters.kind) q.set('kind', filters.kind);
    if (filters.app_ids) for (const id of filters.app_ids) q.append('app_id', id);
    return request('GET', `/api/v1/admin/metrics/histogram?${q.toString()}`);
  },
  async adminMetricsTop(filters: {
    from: number;
    to: number;
    dim: ApiMetricsGroupBy;
    limit?: number;
    app_ids?: string[];
  }): Promise<ApiMetricsTopResponse> {
    const q = new URLSearchParams();
    q.set('from', String(filters.from));
    q.set('to', String(filters.to));
    q.set('dim', filters.dim);
    if (filters.limit !== undefined) q.set('limit', String(filters.limit));
    if (filters.app_ids) for (const id of filters.app_ids) q.append('app_id', id);
    return request('GET', `/api/v1/admin/metrics/top?${q.toString()}`);
  },
  async adminMetricsTopByApp(filters: {
    from: number;
    to: number;
    limit?: number;
    app_ids?: string[];
  }): Promise<ApiMetricsTopByAppResponse> {
    const q = new URLSearchParams();
    q.set('from', String(filters.from));
    q.set('to', String(filters.to));
    if (filters.limit !== undefined) q.set('limit', String(filters.limit));
    if (filters.app_ids) for (const id of filters.app_ids) q.append('app_id', id);
    return request('GET', `/api/v1/admin/metrics/top-by-app?${q.toString()}`);
  },
  async appMetricsHistogram(
    appId: string,
    filters: {
      from: number;
      to: number;
      resolution?: ApiMetricsResolution | 'auto';
      group_by?: ApiMetricsGroupBy[];
      status_class?: ApiMetricsStatusClass;
      method?: ApiMetricsMethod;
      path_prefix?: string;
      /** Filter the histogram to a single user's traffic on this app. */
      user_id?: string;
    },
  ): Promise<ApiMetricsHistogramResponse> {
    const q = new URLSearchParams();
    q.set('from', String(filters.from));
    q.set('to', String(filters.to));
    if (filters.resolution) q.set('resolution', filters.resolution);
    if (filters.group_by) for (const g of filters.group_by) q.append('group_by', g);
    if (filters.status_class) q.set('status_class', filters.status_class);
    if (filters.method) q.set('method', filters.method);
    if (filters.path_prefix) q.set('path_prefix', filters.path_prefix);
    if (filters.user_id) q.set('user_id', filters.user_id);
    return request('GET', `/api/v1/apps/${encodeURIComponent(appId)}/metrics?${q.toString()}`);
  },
  async appMetricsSummary(
    appId: string,
    filters: { from: number; to: number },
  ): Promise<ApiAppMetricsSummary> {
    const q = new URLSearchParams();
    q.set('from', String(filters.from));
    q.set('to', String(filters.to));
    return request(
      'GET',
      `/api/v1/apps/${encodeURIComponent(appId)}/metrics/summary?${q.toString()}`,
    );
  },
  async appMetricsTopUsers(
    appId: string,
    filters: { from: number; to: number; limit?: number },
  ): Promise<ApiAppMetricsTopUsersResponse> {
    const q = new URLSearchParams();
    q.set('from', String(filters.from));
    q.set('to', String(filters.to));
    if (filters.limit !== undefined) q.set('limit', String(filters.limit));
    return request(
      'GET',
      `/api/v1/apps/${encodeURIComponent(appId)}/metrics/top-users?${q.toString()}`,
    );
  },
  async namespaceMetricsTopUsers(
    nsId: string,
    filters: { from: number; to: number; limit?: number },
  ): Promise<ApiNamespaceMetricsTopUsersResponse> {
    const q = new URLSearchParams();
    q.set('from', String(filters.from));
    q.set('to', String(filters.to));
    if (filters.limit !== undefined) q.set('limit', String(filters.limit));
    return request(
      'GET',
      `/api/v1/namespaces/${encodeURIComponent(nsId)}/metrics/top-users?${q.toString()}`,
    );
  },
  async myMetrics(filters: {
    from: number;
    to: number;
    limit?: number;
  }): Promise<ApiMyMetricsResponse> {
    const q = new URLSearchParams();
    q.set('from', String(filters.from));
    q.set('to', String(filters.to));
    if (filters.limit !== undefined) q.set('limit', String(filters.limit));
    return request('GET', `/api/v1/me/metrics?${q.toString()}`);
  },
  async userMetricsTopApps(
    userId: string,
    filters: { from: number; to: number; limit?: number },
  ): Promise<ApiUserMetricsTopAppsResponse> {
    const q = new URLSearchParams();
    q.set('from', String(filters.from));
    q.set('to', String(filters.to));
    if (filters.limit !== undefined) q.set('limit', String(filters.limit));
    return request(
      'GET',
      `/api/v1/users/${encodeURIComponent(userId)}/metrics/top-apps?${q.toString()}`,
    );
  },
  async userOwnedAppMetrics(
    userId: string,
    filters: { from: number; to: number; limit?: number },
  ): Promise<ApiUserOwnedAppMetricsResponse> {
    const q = new URLSearchParams();
    q.set('from', String(filters.from));
    q.set('to', String(filters.to));
    if (filters.limit !== undefined) q.set('limit', String(filters.limit));
    return request('GET', `/api/v1/users/${encodeURIComponent(userId)}/metrics?${q.toString()}`);
  },
  async adminMetricsBackfill(body: {
    app_id: string;
    from_ms?: number;
    to_ms?: number;
    reset?: boolean;
  }): Promise<{
    ok: boolean;
    app_id: string;
    events_applied: number;
    chunks: number;
    reset: boolean;
  }> {
    return request('POST', '/api/v1/admin/metrics/_backfill', body);
  },

  // Admin reconciliation
  async listReconciliationRuns(): Promise<{ runs: ApiReconciliationRunSummary[] }> {
    return request('GET', '/api/v1/admin/reconciliation');
  },
  async getReconciliationRun(
    runId: string,
    resolution?: ApiReconciliationResolution,
  ): Promise<{
    run: {
      run_id: string;
      run_at: number;
      findings_total: number;
      findings_by_kind: Record<string, number>;
    };
    findings: ApiReconciliationFinding[];
  }> {
    const qs = resolution ? `?resolution=${encodeURIComponent(resolution)}` : '';
    return request('GET', `/api/v1/admin/reconciliation/${encodeURIComponent(runId)}${qs}`);
  },
  async listReconciliationIncidents(status?: 'open' | 'resolved' | 'all'): Promise<{
    incidents: ApiReconciliationIncident[];
    summary: ApiReconciliationIncidentSummary;
  }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return request('GET', `/api/v1/admin/reconciliation/incidents${qs}`);
  },
  async applyFinding(id: string): Promise<void> {
    return request('POST', `/api/v1/admin/reconciliation/findings/${encodeURIComponent(id)}/apply`);
  },
  async ignoreFinding(id: string): Promise<void> {
    return request(
      'POST',
      `/api/v1/admin/reconciliation/findings/${encodeURIComponent(id)}/ignore`,
    );
  },
  async syncDnsRecords(): Promise<{ result: ApiDnsSyncResult }> {
    return request('POST', '/api/v1/admin/dns/sync');
  },
  async getNamespaceDnsStatus(nsId: string): Promise<ApiDnsStatus> {
    return request('GET', `/api/v1/namespaces/${nsId}/dns-status`);
  },
  async getAdminDnsStatuses(): Promise<ApiAdminDnsStatuses> {
    return request('GET', '/api/v1/admin/dns-status');
  },

  // Personal access tokens (CLI auth — Control Plane)
  async listMyTokens(): Promise<{ tokens: ApiPersonalAccessToken[] }> {
    return request('GET', '/api/v1/me/tokens');
  },
  async createMyToken(body: {
    name: string;
    expires_in_days?: number;
    provenance?: ApiTokenProvenance;
  }): Promise<{ token: ApiPersonalAccessToken; plaintext: string }> {
    return request('POST', '/api/v1/me/tokens', body);
  },
  async revokeMyToken(id: string): Promise<void> {
    return request('DELETE', `/api/v1/me/tokens/${id}`);
  },

  // App access tokens (Front Door — scoped to a single app)
  async listAppTokens(appId: string): Promise<{ tokens: ApiPersonalAccessToken[] }> {
    return request('GET', `/api/v1/apps/${appId}/tokens`);
  },
  async createAppToken(
    appId: string,
    body: { name: string; expires_in_days?: number },
  ): Promise<{ token: ApiPersonalAccessToken; plaintext: string }> {
    return request('POST', `/api/v1/apps/${appId}/tokens`, body);
  },
  async revokeAppToken(appId: string, tokenId: string): Promise<void> {
    return request('DELETE', `/api/v1/apps/${appId}/tokens/${tokenId}`);
  },

  // Webhook URL tokens (Front Door — mg_wt_, scoped to a worker app + /__webhook/)
  async listWebhookTokens(appId: string): Promise<{ tokens: ApiPersonalAccessToken[] }> {
    return request('GET', `/api/v1/apps/${appId}/webhook-tokens`);
  },
  async createWebhookToken(
    appId: string,
    body: { name: string; webhook_label: string; expires_in_days?: number },
  ): Promise<{ token: ApiPersonalAccessToken; plaintext: string }> {
    return request('POST', `/api/v1/apps/${appId}/webhook-tokens`, body);
  },
  async revokeWebhookToken(appId: string, tokenId: string): Promise<void> {
    return request('DELETE', `/api/v1/apps/${appId}/webhook-tokens/${tokenId}`);
  },
  async webhookTokenActivity(appId: string, tokenId: string): Promise<ApiWebhookTokenActivity> {
    return request('GET', `/api/v1/apps/${appId}/webhook-tokens/${tokenId}/activity`);
  },
  // Deploy keys (CI/CD — mg_deploy_, non-expiring, namespace- or app-scoped)
  async listNamespaceDeployKeys(nsId: string): Promise<{ tokens: ApiPersonalAccessToken[] }> {
    return request('GET', `/api/v1/namespaces/${nsId}/deploy-keys`);
  },
  async createNamespaceDeployKey(
    nsId: string,
    body: { name: string },
  ): Promise<{ token: ApiPersonalAccessToken; plaintext: string }> {
    return request('POST', `/api/v1/namespaces/${nsId}/deploy-keys`, body);
  },
  async revokeNamespaceDeployKey(nsId: string, tokenId: string): Promise<void> {
    return request('DELETE', `/api/v1/namespaces/${nsId}/deploy-keys/${tokenId}`);
  },
  async listAppDeployKeys(appId: string): Promise<{ tokens: ApiPersonalAccessToken[] }> {
    return request('GET', `/api/v1/apps/${appId}/deploy-keys`);
  },
  async createAppDeployKey(
    appId: string,
    body: { name: string },
  ): Promise<{ token: ApiPersonalAccessToken; plaintext: string }> {
    return request('POST', `/api/v1/apps/${appId}/deploy-keys`, body);
  },
  async revokeAppDeployKey(appId: string, tokenId: string): Promise<void> {
    return request('DELETE', `/api/v1/apps/${appId}/deploy-keys/${tokenId}`);
  },

  // Global app tokens (Front Door — scoped to the minting user across all apps)
  async listMyAppTokens(): Promise<{ tokens: ApiPersonalAccessToken[] }> {
    return request('GET', '/api/v1/me/app-tokens');
  },
  async createMyAppToken(body: {
    name: string;
    expires_in_days?: number;
  }): Promise<{ token: ApiPersonalAccessToken; plaintext: string }> {
    return request('POST', '/api/v1/me/app-tokens', body);
  },
  async revokeMyAppToken(id: string): Promise<void> {
    return request('DELETE', `/api/v1/me/app-tokens/${id}`);
  },

  // MCP client registry (Plan 4)
  async listMcpClients(): Promise<ApiMcpClient[]> {
    const res = await request<{ clients: ApiMcpClient[] }>('GET', '/api/v1/mcp/clients');
    return res.clients;
  },
  async createMcpClient(body: {
    client_name: string;
    redirect_uris: string[];
  }): Promise<ApiMcpClient> {
    const res = await request<{ client: ApiMcpClient }>('POST', '/api/v1/mcp/clients', body);
    return res.client;
  },
  async disableMcpClient(clientId: string): Promise<void> {
    return request('DELETE', `/api/v1/mcp/clients/${encodeURIComponent(clientId)}`);
  },

  // MCP grant management (Plan 4)
  async listMcpGrants(): Promise<ApiMcpGrant[]> {
    const res = await request<{ grants: ApiMcpGrant[] }>('GET', '/api/v1/mcp/grants');
    return res.grants;
  },
  async revokeMcpGrant(clientId: string): Promise<void> {
    return request('DELETE', `/api/v1/mcp/grants/${encodeURIComponent(clientId)}`);
  },

  // MCP OAuth consent proxy (calls CP → MCP server-to-server)
  async getMcpConsent(reqId: string): Promise<ApiMcpConsentDetails> {
    return request('GET', `/api/v1/mcp/consent/${encodeURIComponent(reqId)}`);
  },
  async approveMcpConsent(reqId: string): Promise<{ redirect_to: string }> {
    return request('POST', `/api/v1/mcp/consent/${encodeURIComponent(reqId)}/approve`);
  },
  async denyMcpConsent(reqId: string): Promise<{ redirect_to: string }> {
    return request('POST', `/api/v1/mcp/consent/${encodeURIComponent(reqId)}/deny`);
  },

  // MCP team-deploy confirmation (human-in-the-loop opt-in for team/brand deploys)
  async getTeamDeployConfirmation(reqId: string): Promise<{
    req_id: string;
    namespace: { id: string; stub: string; tier: 'team' | 'brand'; display_name: string | null };
    hint_name: string | null;
    app_id: string | null;
    client_name: string | null;
    approved: boolean;
    approved_at: number | null;
    last_used_at: number | null;
    expires_at: number;
    expired: boolean;
  }> {
    return request('GET', `/api/v1/mcp/team-deploy-confirmations/${encodeURIComponent(reqId)}`);
  },
  async approveTeamDeployConfirmation(reqId: string): Promise<{ ok: true; approved_at: number }> {
    return request(
      'POST',
      `/api/v1/mcp/team-deploy-confirmations/${encodeURIComponent(reqId)}/approve`,
    );
  },

  // vibe CLI device-authorization (headless `vibe login`) — approve-page endpoints.
  async getCliAuthRequest(reqId: string): Promise<ApiCliDeviceAuthRequest> {
    return request('GET', `/api/v1/cli-auth-requests/${encodeURIComponent(reqId)}`);
  },
  async approveCliAuthRequest(
    reqId: string,
    userCode: string,
  ): Promise<{ ok: true; approved_at: number }> {
    return request('POST', `/api/v1/cli-auth-requests/${encodeURIComponent(reqId)}/approve`, {
      user_code: userCode,
    });
  },

  // Browser-mediated CLI login. Approval returns a one-use handoff code for
  // the loopback listener; the browser never receives the resulting PAT.
  async getCliLoopbackAuthRequest(reqId: string): Promise<CliLoopbackAuthRequest> {
    return request('GET', `/api/v1/cli-loopback-auth-requests/${encodeURIComponent(reqId)}`);
  },
  async approveCliLoopbackAuthRequest(reqId: string): Promise<CliLoopbackAuthApproval> {
    return request(
      'POST',
      `/api/v1/cli-loopback-auth-requests/${encodeURIComponent(reqId)}/approve`,
    );
  },

  // Request a reusable, fixed-deadline grant bound to one exact create/deploy target.
  async createTeamDeployConfirmation(
    nsId: string,
    target: { app_id: string } | { app_name: string },
  ): Promise<{
    req_id: string;
    app_id: string | null;
    hint_name: string;
    confirmation_url: string;
    expires_at: number;
  }> {
    return request(
      'POST',
      `/api/v1/namespaces/${encodeURIComponent(nsId)}/team-deploy-confirmations`,
      target,
    );
  },

  // Dev convenience
  async seed(): Promise<{ seeded: boolean; summary: Record<string, number> }> {
    return request('POST', '/__seed');
  },
  async reset(): Promise<void> {
    return request('POST', '/__reset');
  },

  // Preview users (admin-only) — synthetic accounts for ACL testing.
  async listPreviewUsers(): Promise<{ preview_users: ApiPreviewUser[] }> {
    return request('GET', '/api/v1/admin/preview-users');
  },
  async createPreviewUser(body: { display_name: string }): Promise<{
    preview_user: ApiPreviewUser;
  }> {
    return request('POST', '/api/v1/admin/preview-users', body);
  },
  async deletePreviewUser(userId: string): Promise<void> {
    return request('DELETE', `/api/v1/admin/preview-users/${encodeURIComponent(userId)}`);
  },
  async putPreviewMembership(
    userId: string,
    namespaceId: string,
    role: 'viewer' | 'member' | 'deploy' | 'namespace_admin',
  ): Promise<void> {
    return request(
      'PUT',
      `/api/v1/admin/preview-users/${encodeURIComponent(userId)}/memberships/${encodeURIComponent(namespaceId)}`,
      { role },
    );
  },
  async deletePreviewMembership(userId: string, namespaceId: string): Promise<void> {
    return request(
      'DELETE',
      `/api/v1/admin/preview-users/${encodeURIComponent(userId)}/memberships/${encodeURIComponent(namespaceId)}`,
    );
  },
  async addPreviewGroupMember(userId: string, groupId: string, role: 'member' | 'admin' = 'member'): Promise<void> {
    return request(
      'PUT',
      `/api/v1/admin/preview-users/${encodeURIComponent(userId)}/groups/${encodeURIComponent(groupId)}`,
      { role },
    );
  },
  async removePreviewGroupMember(userId: string, groupId: string): Promise<void> {
    return request(
      'DELETE',
      `/api/v1/admin/preview-users/${encodeURIComponent(userId)}/groups/${encodeURIComponent(groupId)}`,
    );
  },

  // Guest classification (platform-admin only). Server enforces evidence,
  // freshness and preconditions; 409 means current server state changed.
  async previewUserClassRepair(): Promise<UserClassRepairPreview> {
    return request('GET', '/api/v1/admin/user-class/repair-preview');
  },
  async applyUserClassRepair(body: UserClassRepairApplyInput): Promise<UserClassRepairApplyResponse> {
    return request('POST', '/api/v1/admin/user-class/repair-apply', body);
  },
  async listGuestGroups(): Promise<GuestGroupsResponse> {
    return request('GET', '/api/v1/admin/guest-groups');
  },
  async refreshGuestEvidence(expectedRevision: number): Promise<GuestGroupsResponse> {
    return request('POST', '/api/v1/admin/guest-groups/evidence', {
      expected_revision: expectedRevision,
    });
  },
  async previewGuestGroupImpact(groupPrincipalId: string, operation: 'add' | 'remove'): Promise<GuestGroupImpact> {
    return request('GET', `/api/v1/admin/guest-groups/${encodeURIComponent(groupPrincipalId)}/impact?operation=${operation}`);
  },
  async addGuestGroup(groupPrincipalId: string, expectedRevision: number, expectedImpactDigest: string): Promise<GuestGroupsResponse> {
    return request('PUT', `/api/v1/admin/guest-groups/${encodeURIComponent(groupPrincipalId)}`, {
      expected_revision: expectedRevision,
      expected_impact_digest: expectedImpactDigest,
    });
  },
  async removeGuestGroup(groupPrincipalId: string, expectedRevision: number, expectedImpactDigest: string): Promise<GuestGroupsResponse> {
    return request('DELETE', `/api/v1/admin/guest-groups/${encodeURIComponent(groupPrincipalId)}`, {
      expected_revision: expectedRevision,
      expected_impact_digest: expectedImpactDigest,
    });
  },
  async setUserClass(
    userId: string,
    body: SetUserClassInput,
  ): Promise<{ user_id: string; user_class: UserClass; decision_id: string; changed: boolean }> {
    return request('PUT', `/api/v1/admin/users/${encodeURIComponent(userId)}/user-class`, body);
  },
  async resumeAutomaticUserClass(userId: string, body: UserClassResetInput): Promise<UserClassResetResponse> {
    return request('POST', `/api/v1/admin/users/${encodeURIComponent(userId)}/user-class/automatic`, body);
  },
  /**
   * Start a preview session as the given preview user. Replaces the caller's
   * session cookies; the caller should hard-reload after this resolves so the
   * Svelte app picks up the new identity from /api/v1/me.
   */
  async startPreviewSession(userId: string): Promise<void> {
    return request('POST', `/api/v1/admin/preview-users/${encodeURIComponent(userId)}/impersonate`);
  },
  /**
   * Exit the current preview session. Restores the admin's session cookies
   * (recovered from the `mg_preview_origin` cookie) and clears the marker.
   * Caller should hard-reload after this resolves.
   */
  async exitPreviewSession(): Promise<void> {
    return request('POST', '/auth/preview/exit');
  },
};

/**
 * A view over `apiClient` whose calls do NOT touch the `requestProgress`
 * counter, so they never light the top-bar loading indicator. Use this from
 * SvelteKit `load()` functions: hover-preload runs loaders speculatively, and
 * a request for a page the user only *might* visit shouldn't show a loading
 * bar. Real navigations still show the bar via SvelteKit's `navigating` store
 * (see RequestProgress.svelte); foreground in-page requests should keep using
 * `apiClient` so their activity stays visible.
 *
 * Each method sets the module-level `suppressProgress` flag for the synchronous
 * span of the call. `requestRaw` snapshots that flag before its first `await`,
 * so the suppression applies to exactly the request the loader kicked off and
 * not to anything that happens to be in flight when its promise later resolves.
 */
export const apiClientQuiet: typeof apiClient = new Proxy(apiClient, {
  get(target, prop, receiver) {
    const value = Reflect.get(target, prop, receiver);
    if (typeof value !== 'function') return value;
    return (...args: unknown[]) => {
      const prev = suppressProgress;
      suppressProgress = true;
      try {
        return (value as (...a: unknown[]) => unknown).apply(target, args);
      } finally {
        suppressProgress = prev;
      }
    };
  },
});
